import React, { useEffect, useMemo } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { ReactRouterAppProvider } from '@toolpad/core/react-router';
import { AltRoute, Subject } from '@mui/icons-material';
import { DashboardLayout, PageContainer } from '@toolpad/core';

import ScreenLoader from './components/ScreenLoader';
import GlobalSnackbar from './components/GlobalSnackbar';
import AccessDeniedCard from './components/AccessDeniedCard';
import AuthenticatedRoutes from './routes/AuthenticatedRoutes';
import { darkTheme } from './theme';
import { HOME_ROUTE } from './constants/defaults';
import SideBottomBar from './components/SideBottomBar';
import SettingsIcon from '@mui/icons-material/Settings';
import WebhookIcon from '@mui/icons-material/Webhook';
import ApartmentIcon from '@mui/icons-material/Apartment';
import CableIcon from '@mui/icons-material/Cable';
import LoginPage from './resources/login/pages/LoginPage';
import SelectOrganizationPage from './resources/login/pages/SelectOrganizationPage';
import SignupPage from './resources/signup/pages/SignupPage';
import ForgotPasswordPage from './resources/forgotPassword/pages/ForgotPasswordPage';
import { fetchUserRequest } from './api/user/userSlice';
import { fetchOrgRequest } from './api/organizations/orgSlice';
import { fetchSubscriptionRequest } from './api/subscription/subscriptionSlice';
import { useDispatch, useSelector } from 'react-redux';
import { selectRequiresOrgSelection, selectSessionReady, sessionResolved } from './api/auth/authSlice';
import { authApiClient } from './api/axios';
import UserAccount from './components/UserAccount';
import OrgSwitcher from './components/OrgSwitcher';
import { Box, Stack } from '@mui/material';
import appConfig from './config/appConfig';
import { usePermissions } from './hooks/usePermissions';
import { READ_WEBHOOK_CONFIG, READ_WEBHOOK_EVENTS } from './constants/webhookPermissions';
import { canAccessSettings } from './utils/roles';
import EnvironmentBanner from './components/EnvironmentBanner';

const NAVIGATION = [
  {
    segment: 'events',
    title: 'Events',
    icon: <AltRoute />,
    pattern: 'events{/:id}*',
    requirePermission: READ_WEBHOOK_EVENTS,
  },
  {
    segment: 'delivery-logs',
    title: 'Logs',
    icon: <Subject />,
    pattern: 'delivery-logs{/:id}*',
    requirePermission: READ_WEBHOOK_EVENTS,
  },
  {
    segment: 'org-directory',
    title: 'Org Directory',
    icon: <ApartmentIcon />,
    pattern: 'org-directory',
  },
  {
    segment: 'webhooks',
    title: 'Webhooks',
    icon: <WebhookIcon />,
    pattern: 'webhooks{/:id}*',
    requirePermission: READ_WEBHOOK_CONFIG,
  },
  {
    segment: 'connectors',
    title: 'Connectors',
    icon: <CableIcon />,
    pattern: 'connectors{/:connectorKey}*',
  },
  {
    segment: 'settings',
    title: 'Settings',
    icon: <SettingsIcon />,
    pattern: 'settings',
  },
];

// Component to handle redirect to login with original location
const RedirectToLogin = () => {
  const location = useLocation();
  return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
};

const ToolbarAccount = () => {
  return (
    <Stack direction="row" alignItems="center" gap={2}>
      <OrgSwitcher />
      <UserAccount />
    </Stack>
  );
};

const UnauthenticatedLayout = () => (
  <Box
    sx={{
      flex: 1,
      minHeight: 0,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Outlet />
  </Box>
);

const RedirectToSelectOrganization = () => {
  const location = useLocation();

  return <Navigate to="/select-organization" state={{ redirectTo: location.pathname + location.search }} replace />;
};

function App() {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const sessionChecked = useSelector((state) => state.auth.sessionChecked);
  const sessionReady = useSelector(selectSessionReady);
  const requiresOrgSelection = useSelector(selectRequiresOrgSelection);
  const dispatch = useDispatch();
  const orgLoading = useSelector((state) => state.org.loading);
  const subscriptionLoading = useSelector((state) => state.subscription.loading);
  const userLoading = useSelector((state) => state.user.loading);
  const org = useSelector((state) => state.org.org);
  const user = useSelector((state) => state.user.user);
  const subscription = useSelector((state) => state.subscription.subscription);
  const { hasWebhookPermission, hasAnyWebhookPermission, loading: permissionsLoading } = usePermissions();

  let isAllowed = !!org && ((org.is_partner && org.is_embedded) || (!org.is_partner && !org.is_provisioned_by_partner));
  const hasWebhooksAccess = hasAnyWebhookPermission();
  const showSettings = canAccessSettings(user);

  const isCustomConnectorEnabled = Boolean(subscription?.features?.enable_custom_connector);

  const navigation = useMemo(() => {
    if (!isAllowed || !hasWebhooksAccess) {
      return [];
    }

    return NAVIGATION.filter((item) => {
      if (item.segment === 'settings' && !showSettings) {
        return false;
      }
      if (item.segment === 'connectors' && !isCustomConnectorEnabled) {
        return false;
      }
      if (item.requirePermission && !hasWebhookPermission(item.requirePermission)) {
        return false;
      }
      return true;
    });
  }, [isAllowed, hasWebhooksAccess, showSettings, isCustomConnectorEnabled, hasWebhookPermission]);

  useEffect(() => {
    authApiClient
      .get('/me', { skipSessionRedirect: true })
      .then(() => {
        dispatch(sessionResolved({ authenticated: true }));
      })
      .catch(() => {
        dispatch(sessionResolved({ authenticated: false }));
      });
  }, [dispatch]);

  useEffect(() => {
    if (sessionReady) {
      dispatch(fetchOrgRequest());
      dispatch(fetchSubscriptionRequest());
      dispatch(fetchUserRequest());
    }
  }, [dispatch, sessionReady]);

  if (!sessionChecked || (sessionReady && (orgLoading || subscriptionLoading || userLoading || permissionsLoading))) {
    return <ScreenLoader />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        maxHeight: '100dvh',
        overflow: 'hidden',
      }}
    >
      <EnvironmentBanner />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ReactRouterAppProvider theme={darkTheme} navigation={navigation}>
          <AuthenticatedApp
            sessionReady={sessionReady}
            isAuthenticated={isAuthenticated}
            requiresOrgSelection={requiresOrgSelection}
            isAllowed={isAllowed}
            hasWebhooksAccess={hasWebhooksAccess}
          />
          <ScreenLoader />
          <GlobalSnackbar />
        </ReactRouterAppProvider>
      </Box>
    </Box>
  );
}

function HomeRedirect() {
  const user = useSelector((state) => state.user.user);
  const { hasWebhookPermission } = usePermissions();

  if (hasWebhookPermission(READ_WEBHOOK_EVENTS)) {
    return <Navigate to="/events" replace />;
  }
  if (hasWebhookPermission(READ_WEBHOOK_CONFIG)) {
    return <Navigate to="/webhooks" replace />;
  }
  if (canAccessSettings(user)) {
    return <Navigate to="/settings" replace />;
  }

  return <Navigate to="/org-directory" replace />;
}

function AuthenticatedApp({ sessionReady, isAuthenticated, requiresOrgSelection, isAllowed, hasWebhooksAccess }) {
  return (
    <Routes>
      {sessionReady ? (
        <Route
          path="/*"
          element={
            <DashboardLayout
              sx={{ flex: 1, minHeight: 0, width: '100%', overflow: 'hidden' }}
              sidebarExpandedWidth={250}
              slots={{
                toolbarAccount: ToolbarAccount,
                sidebarFooter: SideBottomBar,
              }}
              branding={{
                title: '',
                homeUrl: HOME_ROUTE,
                logo: <img src={appConfig.brandLogoPath} alt={`${appConfig.brandName} logo`} />,
              }}
            >
              {isAllowed && hasWebhooksAccess ? (
                <PageContainer
                  maxWidth={'xxl'}
                  breadcrumbs={[]}
                  title=""
                  sx={{ overflow: 'hidden', px: '0 !important' }}
                >
                  <Routes>
                    <Route path="/" element={<HomeRedirect />} />
                    {AuthenticatedRoutes()}
                  </Routes>
                </PageContainer>
              ) : isAllowed && !hasWebhooksAccess ? (
                <AccessDeniedCard title="Developer Dashboard" />
              ) : (
                <div style={{ width: '100%', height: '100%' }} />
              )}
            </DashboardLayout>
          }
        />
      ) : isAuthenticated && requiresOrgSelection ? (
        <>
          <Route path="/select-organization" element={<SelectOrganizationPage />} />
          <Route path="*" element={<RedirectToSelectOrganization />} />
        </>
      ) : (
        <Route element={<UnauthenticatedLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="*" element={<RedirectToLogin />} />
        </Route>
      )}
    </Routes>
  );
}

export default App;
